import { ReactNode } from "react";

// Atlas Tooltip does not add padding around ReactNode content (only around
// raw strings). Apply our own padding here so every tooltip body breathes.
const renderList = (intro: string, items: string[]): ReactNode => (
  <div className="max-w-[280px] px-3 py-2 text-left">
    <div className="mb-1">{intro}</div>
    <ul className="list-disc pl-4 space-y-0.5">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  </div>
);

const renderText = (text: string): ReactNode => (
  <div className="max-w-[280px] px-3 py-2 text-left">{text}</div>
);

export const COLUMN_HEADER_TOOLTIPS: Record<string, ReactNode> = {
  notes: renderText(
    "The total number of CareQueue notes for the resident",
  ),
  daysSinceLastReview: renderText(
    "The total number of days since the resident was reviewed within CareQueue",
  ),
  vital_changes: renderList(
    "Flagged if any of these occur within the selected time window:",
    [
      "Weight loss ≥ 5%",
      "Systolic BP > 180 or < 90",
      "Diastolic BP > 110 or < 60",
      "Heart rate > 120 or < 50 bpm",
      "Temperature > 101°F or < 96°F",
    ],
  ),
  progress_notes: renderList(
    "AI reviews each note and classifies HIGH or LOW concern based on notes created within the selected time window:",
    [
      "Falls or fall risk",
      "Mental status / cognitive changes",
      "Signs of infection",
      "Pain or medication refusal",
      "Emergency transfers",
      "Aggressive behavior",
      "Skin breakdown",
      "Abnormal vitals, weight loss, dehydration",
    ],
  ),
  high_risk_meds: renderList(
    "AI classifies each medication started within the selected time window against high-risk categories:",
    [
      "Anticoagulants",
      "Insulin",
      "Opiates / Narcotics",
      "Benzodiazepines",
      "Psychotropics",
    ],
  ),
  low_risk_meds: renderText(
    "Medications started within the selected time window that do not match any high-risk category",
  ),
  infections: renderList(
    "Flagged if any of the following is found within the selected time window:",
    [
      "An unresolved infection record exists",
      "An infection-type incident was reported",
      "A progress note mentions: infection, sepsis, UTI, urinary tract, pneumonia, respiratory infection, wound infection, fever, antibiotic",
    ],
  ),
  falls: renderList(
    "Flagged if any of the following is found within the selected time window:",
    [
      'Incident coded as "fall"',
      "Description within progress notes containing: fall, fell, falling, slip, trip, found on floor",
    ],
  ),
  adl_increase: renderText(
    "Flagged if there are 1 or more declines within the Self-care measures found within the selected time window",
  ),
  locomotion_decline: renderText(
    "Flagged if there are 1 or more declines within the Mobility measures found within the selected time window",
  ),
  indwelling_catheter: renderText(
    "Flagged if there is an active catheter order",
  ),
  previous_falls: renderList(
    "Flagged if any of the following is found prior to the most recent 30 days:",
    [
      'Incident coded as "fall"',
      "Description within progress notes containing: fall, fell, falling, slip, trip, found on floor",
    ],
  ),
  receiving_antipsychotic: renderText(
    "Flagged if there are 1 or more active antipsychotic orders",
  ),
  long_term_infections: renderText(
    "Flagged if an unresolved infection record exists",
  ),
};

export function getColumnHeaderTooltip(key: string): ReactNode | undefined {
  return COLUMN_HEADER_TOOLTIPS[key];
}
